import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import Image from "next/image";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { Activity, ChevronRight, GaugeCircleIcon, HardDrive, History, Layers, Ruler, Terminal } from "lucide-react";
import MigrationWizardSvg from "@/assets/migration-wizard.svg";
import { AppSidebarHeaderSkeleton } from "../ui/app-header";
import { RAIL_ROW_ACTIVE } from "./SidebarGlobalChrome";
import { isSettingsPath } from "./useActiveApp";
import { cn } from "@repo/blacksmith-ui/cn";
import { readCachedSidebarVariant } from "../codesmith/sidebarVariant";
interface SidebarSkeletonProps {
  collapsed?: boolean;
  /** Standalone account pages (usage/settings/support/admin): the Actions
   * sidebar hides its nav there, so the skeleton omits the migration and
   * nav rows. */
  standalone?: boolean;
}

/**
 * Skeleton for the GitHub Actions contextual sidebar. Matches Sidebar.tsx
 * layout and DESIGN.md (including avoiding layout shift).
 *
 * The sidebar's chrome and nav are fixed: none of it depends on the data being
 * awaited, so every fixed glyph and label renders as the mounted sidebar's own
 * content rather than a gray block that would only flash before the identical
 * row lands (the same treatment as RailSkeleton in SidebarLayout.tsx).
 */
type SkeletonNavItem = {
  name: string;
  icon: ReactNode;
  trailing?: "chevron";
};

// Mirrors Sidebar's MainNav: hover-card rows (Run History, Analytics,
// Storage) show a trailing chevron.
const mainNav: SkeletonNavItem[] = [{
  name: "Run History",
  icon: <History className="size-[18px]" />,
  trailing: "chevron"
}, {
  name: "Monitors",
  icon: <Activity className="size-[18px]" />
}, {
  name: "Rightsize Runners",
  icon: <Ruler className="size-[18px]" />
}, {
  name: "Machine Definitions",
  icon: <Layers className="size-[18px]" />
}, {
  name: "Logs",
  icon: <Terminal className="size-[18px]" />
}, {
  name: "Analytics",
  icon: <GaugeCircleIcon className="size-[18px]" />,
  trailing: "chevron"
}, {
  name: "Storage",
  icon: <HardDrive className="size-[18px]" />,
  trailing: "chevron"
}];

/** Mirrors Sidebar's route-derived active nav item (via isActive) so the
 * active row's fill doesn't pop in on swap. Route templates are enough here:
 * the org segment carries no signal for which section is active. */
function activeNavName(pathname: string): string | null {
  const path = pathname.replace(/^\/\[org\]/, "") || "/";
  if (path === "/" || path.startsWith("/runs")) return "Run History";
  if (path.startsWith("/monitors")) return "Monitors";
  if (path.startsWith("/rightsize")) return "Rightsize Runners";
  if (path.startsWith("/machine-definitions")) return "Machine Definitions";
  if (path.startsWith("/logs")) return "Logs";
  if (path.startsWith("/analytics") || path.startsWith("/docker-analytics")) {
    return "Analytics";
  }
  if (path.startsWith("/stickydisks") || path.startsWith("/cache")) {
    return "Storage";
  }
  return null;
}

/** Non-interactive twin of the mounted sidebar's expanded nav row: same 32px
 * footprint, icon, label, and trailing chevron, at resting colors,
 * without the hover/focus affordances a placeholder shouldn't advertise. */
function NavRowSkeleton(t0) {
  const $ = _c(11);
  const {
    item,
    active
  } = t0;
  const t1 = active && RAIL_ROW_ACTIVE;
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex h-8 w-full items-center gap-2 rounded-md border border-transparent p-1.5 type-body-s-regular", "text-neutral-800 [&_svg]:text-neutral-600 dark:text-neutral-300 dark:[&_svg]:text-neutral-400", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== item.name) {
    t3 = <span className="whitespace-nowrap">{item.name}</span>;
    $[2] = item.name;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== item.trailing) {
    t4 = item.trailing === "chevron" && <ChevronRight className="ml-auto size-4 text-neutral-500 dark:text-neutral-400" />;
    $[4] = item.trailing;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== item.icon || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <div className={t2}>{item.icon}{t3}{t4}</div>;
    $[6] = item.icon;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/** Collapsed twin: the 32px icon-only button at resting colors. */
function CollapsedNavRowSkeleton(t0) {
  const $ = _c(5);
  const {
    item,
    active
  } = t0;
  const t1 = active && RAIL_ROW_ACTIVE;
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-md", "[&_svg]:text-neutral-600 dark:[&_svg]:text-neutral-400", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== item.icon || $[3] !== t2) {
    t3 = <div className={t2}>{item.icon}</div>;
    $[2] = item.icon;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/** MigrationWizard's own icon + label at its row geometry (rounded-sm and its
 * bespoke resting colors, matching the trigger button in LazyMigrationWizard.tsx). */
function MigrationRowSkeleton(t0) {
  const $ = _c(3);
  const {
    collapsed
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="flex size-[18px] shrink-0 items-center justify-center"><Image src={MigrationWizardSvg} alt="" aria-hidden="true" width={16} height={16} className="size-4" /></span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const icon = t1;
  if (collapsed) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm">{icon}</div>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex h-8 w-full items-center gap-2 rounded-sm border border-transparent p-1.5 type-body-s-regular text-[#434546] dark:text-[#bbb]">{icon}<span className="whitespace-nowrap">Migration Wizard</span></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

/** The migration row + nav rows shared by every non-standalone variant. */
function NavBlockSkeleton(t0) {
  const $ = _c(8);
  const {
    collapsed,
    activeName
  } = t0;
  let t1;
  if ($[0] !== collapsed) {
    t1 = <MigrationRowSkeleton collapsed={collapsed} />;
    $[0] = collapsed;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== activeName || $[3] !== collapsed) {
    t2 = mainNav.map(item => collapsed ? <CollapsedNavRowSkeleton key={item.name} item={item} active={activeName === item.name} /> : <NavRowSkeleton key={item.name} item={item} active={activeName === item.name} />);
    $[2] = activeName;
    $[3] = collapsed;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export default function SidebarSkeleton(t0) {
  const $ = _c(28);
  const {
    collapsed,
    standalone
  } = t0;
  const router = useRouter();
  let t1;
  if ($[0] !== router.pathname) {
    t1 = activeNavName(router.pathname);
    $[0] = router.pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activeName = t1;
  const urlOrg = router.query.org;
  const redesign = readCachedSidebarVariant(typeof urlOrg === "string" ? urlOrg : null) === "redesign";
  const headerGapClass = redesign ? "pt-0" : "pt-app-header-gap";
  const rowGapClass = redesign ? "gap-0" : "gap-1";
  const title = standalone ? isSettingsPath(router.pathname) ? "Settings" : null : "GitHub Actions";
  if (collapsed) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <AppSidebarHeaderSkeleton collapsed={true} />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== headerGapClass) {
      t3 = cn("flex flex-1 flex-col justify-start min-h-0 overflow-hidden pb-0 pl-3 pr-1 w-full", headerGapClass);
      $[3] = headerGapClass;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        minHeight: "200px"
      };
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== activeName || $[7] !== rowGapClass || $[8] !== standalone) {
      t5 = !standalone && <div className={cn("-mx-1 flex flex-1 flex-col items-center min-h-0", rowGapClass)}><NavBlockSkeleton collapsed={true} activeName={activeName} /></div>;
      $[6] = activeName;
      $[7] = rowGapClass;
      $[8] = standalone;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t3 || $[11] !== t5) {
      t6 = <div className="flex h-full w-[var(--sidebar-width-icon)] flex-col pointer-events-none border-r border-[#0c0c0d14] dark:border-white/12 bg-sidebar" data-slot="sidebar-skeleton">{t2}<div className={t3} style={t4}>{t5}</div></div>;
      $[10] = t3;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  const t2 = title ?? undefined;
  let t3;
  if ($[13] !== t2) {
    t3 = <AppSidebarHeaderSkeleton title={t2} />;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== headerGapClass) {
    t4 = cn("flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-0", headerGapClass);
    $[15] = headerGapClass;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      minHeight: "200px"
    };
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== activeName || $[19] !== rowGapClass || $[20] !== standalone) {
    t6 = !standalone && <div className={cn("flex flex-col", rowGapClass)}><NavBlockSkeleton activeName={activeName} /></div>;
    $[18] = activeName;
    $[19] = rowGapClass;
    $[20] = standalone;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t4 || $[23] !== t6) {
    t7 = <div className={t4} style={t5}>{t6}</div>;
    $[22] = t4;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t3 || $[26] !== t7) {
    t8 = <div className="flex h-full w-[var(--sidebar-width)] flex-col pointer-events-none border-r border-[#0c0c0d14] dark:border-white/12 bg-sidebar" data-slot="sidebar-skeleton">{t3}{t7}</div>;
    $[25] = t3;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
}